import { useMemo } from 'react'

import type { DirectoryRelease } from '@/lib/directory/aggregate-releases'
import type { FilterOptions } from '@/lib/directory/build-filter-options'
import type { DeferredDirectoryCatalog } from '@/lib/directory/deferred-catalog'
import { countFacets } from '@/lib/directory/facet-counts'
import { buildFacetLabels } from '@/lib/directory/facet-labels'
import { filterReleases, paginateReleases, sortReleases } from '@/lib/directory/present-releases'

import type { DirectoryState } from './use-directory-reducer'

export const useDirectoryPresentation = (
  releases: DirectoryRelease[],
  filterOptions: FilterOptions,
  state: DirectoryState,
  ready: boolean,
  deferred: DeferredDirectoryCatalog | undefined
) => {
  const results = useMemo(
    () => (ready ? sortReleases(filterReleases(releases, state.filters, state.term), state.sort) : releases),
    [ready, releases, state.filters, state.term, state.sort]
  )
  const paged = useMemo(
    () =>
      !ready && deferred ? { ...deferred.page, items: results } : paginateReleases(results, state.page, state.pageSize),
    [ready, deferred, results, state.page, state.pageSize]
  )
  const summary = useMemo(
    () =>
      !ready && deferred
        ? deferred
        : {
            totalCount: releases.length,
            modelCount: results.reduce((sum, release) => sum + release.modelCount, 0),
            totalModelCount: releases.reduce((sum, release) => sum + release.modelCount, 0),
          },
    [ready, deferred, releases, results]
  )
  const counts = useMemo(
    () => (!ready && deferred ? deferred.counts : countFacets(releases, filterOptions, state.filters, state.term)),
    [ready, deferred, releases, filterOptions, state.filters, state.term]
  )
  const facetLabels = useMemo(() => buildFacetLabels(filterOptions), [filterOptions])
  return { results, paged, counts, facetLabels, ...summary }
}
